// src/App.jsx
import React, { useEffect, useState } from 'react';
// ⛔️ REMOVED 'BrowserRouter as Router' FROM THIS IMPORT
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { APP_VERSION } from './version'; // 🔁 version for auto-update

// Components
import ProtectedRoute from './components/ProtectedRoute';

// Layouts
import AuthLayout from './layouts/AuthLayout';
import AdminLayout from './layouts/AdminLayout';
import UnderwriterLayout from './layouts/UnderwriterLayout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SVARPage from './pages/SVARPage';
import DisqualifiedPolicies from './pages/DisqualifiedPolicies';
import TicketingSystem from './pages/TicketingSystem';
import AdminDashboard from './pages/AdminDashboard';
import AdminTickets from './pages/AdminTickets';
import SupervisorDashboard from './pages/SupervisorDashboard';
import OfficeNumbers from './pages/OfficeNumbers';
import SupervisorTickets from './pages/SupervisorTickets';
import EODReport from './pages/EODReport';
import EODHistory from './pages/EODHistory';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminManageUsers from './pages/AdminManageUsers';
import EnterViolation from './pages/admin/EnterViolation';
import ManageViolations from './pages/admin/ManageViolations';
import AgentViolations from './pages/AgentViolations';

// ✅ ADDED: Admin Commission Log page import
import AdminCommissionLog from './pages/admin/AdminCommissionLog';

// ✅ ADDED: Agent Tax Commission Log page import
import AgentCommissionLog from './pages/agent/AgentCommissionLog';
// ✅ ADDED: Agent Insurance Commission page import
import AgentCommission from './pages/agent/AgentCommission';
import AgentQuoteLog from './pages/agent/AgentQuoteLog';

// ✅ ADDED: Commission Uploader page import
import CommissionUploader from './pages/admin/CommissionUploader';

// ✅ ADDED: Marketing Operations Center page import
import MarketingOps from './pages/admin/MarketingOps';

// ✅ ADDED: Admin Tax WIP page import
import AdminTaxWip from './pages/admin/AdminTaxWip';

// ✅ ADDED: Admin Quote Log
import AdminQuoteLog from './pages/admin/AdminQuoteLog';

// Office Management (independent admin module)
import OfficeManagement from './pages/admin/officeManagement/OfficeManagement';
import OfficeProfile from './pages/admin/officeManagement/OfficeProfile';
import CompanyServices from './pages/admin/officeManagement/CompanyServices';
import CompanyContacts from './pages/admin/officeManagement/CompanyContacts';
import ServiceProviders from './pages/admin/officeManagement/ServiceProviders';
import LandlordDirectory from './pages/admin/officeManagement/LandlordDirectory';


// ✅ ADDED: Supervisor Tax WIP page import
import SupervisorTaxWip from './pages/supervisor/SupervisorTaxWip';

// ✅ ADDED: Supervisor Quote Operations
import SupervisorQuoteOperations from './pages/supervisor/SupervisorQuoteOperations';

// ✅ ADDED: Regional pages
import RegionalDashboard from './pages/regional/RegionalDashboard';
import RegionalQuoteOperations from './pages/regional/RegionalQuoteOperations';
import RegionalTickets from './pages/regional/RegionalTickets';

// Underwriting pages
import UnderwritingSubmit from './pages/agent/UnderwritingSubmit';
import UnderwritingDashboard from './pages/uw/UnderwritingDashboard';
import UnderwritingLog from './pages/uw/UnderwritingLog';
import PendingUnderwriting from './pages/uw/PendingUnderwriting';
import UnderwritingScoreboard from './pages/uw/UnderwritingScoreboard';
import UnderwritingOfficePacing from './pages/uw/UnderwritingOfficePacing';
import RetentionQueue from './pages/uw/RetentionQueue';

// ✅ ADDED: Tax Reconciliation page import
import TaxReconciliation from './pages/uw/TaxReconciliation';


// Admin EODs
import OfficeEODs from './components/OfficeEODs';

/** 🔐 Lock / idle settings – keep in sync with AuthContext */
const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes idle → soft lock
const LS_LAST_ACTIVITY = 'auth_last_activity';
const LS_SOFT_LOCK = 'auth_soft_lock';

// ⏳ How long the lock screen waits before auto-logout
const LOCKSCREEN_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes on lock screen before auto-logout

/**
 * Simple lock screen that shows when:
 * - There *is* a user/session
 * - And either:
 * - The idle timer set a soft lock flag, or
 * - Their last activity is older than IDLE_TIMEOUT_MS (stale cookies / next day)
 *
 * This protects you from the “new agent opens browser and sees previous
 * agent’s dashboard immediately” problem, and gives a “still using this?”
 * prompt before full logout.
 */
function LockScreen({ onContinue }) {
  const { user, signOut, supabaseClient } = useAuth();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [remainingMs, setRemainingMs] = useState(LOCKSCREEN_TIMEOUT_MS);

  // ⏳ Start a countdown when the lock screen appears
  useEffect(() => {
    const startedAt = Date.now();

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const left = LOCKSCREEN_TIMEOUT_MS - elapsed;

      if (left <= 0) {
        clearInterval(intervalId);
        // Time's up → hard logout
        signOut();
      } else {
        setRemainingMs(left);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [signOut]);

  const handleSwitchUser = async () => {
    await signOut(); // will redirect them back to login via your existing logic
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const email = user?.email || user?.user_metadata?.email;

    if (!email) {
      setError('Cannot determine user email. Please switch user and log in again.');
      return;
    }

    try {
      setSubmitting(true);

      const { error: signInError } = await supabaseClient.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        console.error('[LockScreen] re-auth error:', signInError);
        setError('Incorrect password. Please try again.');
        return;
      }

      // ✅ Password is correct → refresh activity + clear soft lock + unlock UI
      const now = Date.now();
      try {
        localStorage.setItem(LS_LAST_ACTIVITY, String(now));
        localStorage.removeItem(LS_SOFT_LOCK);
      } catch {
        // ignore
      }

      setPassword('');
      onContinue(); // just sets showLock(false) in AppRoutes
    } catch (err) {
      console.error('[LockScreen] unexpected re-auth error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const label =
    user?.email ||
    user?.user_metadata?.full_name ||
    user?.user_metadata?.email ||
    'agent';

  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow p-6 space-y-4">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-semibold">Screen Locked</h1>
          <p className="text-gray-600">
            Signed in as <strong>{label}</strong>
          </p>
          <p className="text-sm text-gray-500">
            For shared desks, please confirm it&apos;s you by entering your password,
            or switch user.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              type="password"
              className="w-full rounded-xl border px-3 py-2 text-sm"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              autoFocus
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">
              {error}
            </p>
          )}

          <div className="space-y-2">
            <button
              type="submit"
              disabled={!password || submitting}
              className="w-full rounded-xl bg-black text-white py-2 disabled:opacity-60"
            >
              {submitting ? 'Checking…' : 'Continue as this user'}
            </button>
            <button
              type="button"
              onClick={handleSwitchUser}
              disabled={submitting}
              className="w-full rounded-xl border py-2"
            >
              Switch User
            </button>
          </div>
        </form>

        <p className="text-xs text-gray-500 text-center">
          You will be logged out automatically in <strong>{minutes}:{seconds}</strong>{' '}
          if you don&apos;t unlock this session.
        </p>

        <p className="text-[11px] text-gray-400 text-center mt-1">
          If someone closes the browser without logging out, the next person
          will see this lock screen first before accessing the dashboard.
        </p>
      </div>
    </div>
  );
}

// This component contains all your original routing logic.
function AppRoutes() {
  const { user, profile, loading } = useAuth();
  const [showLock, setShowLock] = useState(false);

  // 🔁 Version check: reload tab if a new deploy is available
  const checkVersion = async () => {
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return;

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return;

      const data = await res.json();
      const serverVersion = data?.version;

      if (serverVersion && serverVersion !== APP_VERSION) {
        // 🛡️ Safety Breaker: Check if we already tried to reload for this specific version
        const lastAttempt = localStorage.getItem('update_attempt_version');

        if (lastAttempt !== serverVersion) {
          // First time detecting this mismatch? Try reloading to sync.
          console.log(`Version mismatch detected (Server: ${serverVersion} vs App: ${APP_VERSION}). Reloading...`);
          localStorage.setItem('update_attempt_version', serverVersion);
          window.location.reload();
        } else {
          // We already reloaded and they STILL don't match? Stop the loop.
          console.warn(`Auto-update loop detected. Staying on App version ${APP_VERSION} despite Server saying ${serverVersion}.`);
        }
      } else {
        // Versions match! Clear the safety flag so future updates work normally.
        localStorage.removeItem('update_attempt_version');
      }
    } catch (e) {
      // quiet in dev
    }
  };

  // 🆕 Auto-update version check (runs immediately + every 5 min)
  useEffect(() => {
    checkVersion(); // run immediately

    const interval = setInterval(checkVersion, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 🔐 Decide if we should show the lock screen when a session exists
  //    Poll localStorage every second while a user is logged in.
  useEffect(() => {
    if (!user) {
      setShowLock(false);
      return;
    }

    const checkLockState = () => {
      try {
        const now = Date.now();

        const rawLast = localStorage.getItem(LS_LAST_ACTIVITY);
        const last = Number(rawLast || 0);

        const rawSoft = localStorage.getItem(LS_SOFT_LOCK);
        const hasSoftLock = !!rawSoft;

        let shouldLock = false;

        // 1) If soft lock flag is set (idle triggered), always lock
        if (hasSoftLock) {
          shouldLock = true;
        }

        // 2) If last activity is stale, auto-soft-lock & show lock screen
        if (!shouldLock && last) {
          const diff = now - last;
          const isStale = diff > IDLE_TIMEOUT_MS;
          if (isStale) {
            shouldLock = true;
            // ensure soft lock flag is set for consistency
            localStorage.setItem(LS_SOFT_LOCK, String(now));
          }
        }

        setShowLock(shouldLock);
      } catch (e) {
        console.warn('[App] lock screen check failed:', e?.message || e);
      }
    };

    // Run immediately once
    checkLockState();
    // Then poll every second
    const intervalId = setInterval(checkLockState, 1000);

    return () => clearInterval(intervalId);
  }, [user]);

  if (loading) return <div>Loading session...</div>;

  const getHomeRoute = () => {
    if (!user || !profile) return '/login';
    const role = profile.role || 'agent';
    switch (role) {
      case 'admin':
        return '/admin';

      case 'supervisor':
        return '/dashboard';

      case 'territory_manager':
        return '/territory/dashboard';

      case 'regional':
        return '/regional/dashboard';

      case 'underwriter':
      case 'uw_manager':
        return '/uw';

      case 'agent':
      default:
        return '/dashboard';
    }
  };

  // 🔐 Gate: if the session is locked, show LockScreen instead of routes
  if (user && showLock) {
    return <LockScreen onContinue={() => setShowLock(false)} />;
  }

  // ⛔️ REMOVED THE <Router> WRAPPER FROM AROUND <Routes>
  // NOTE: Make sure BrowserRouter is wrapping <App /> in your src/main.jsx or src/index.jsx.
  return (
    <Routes>
      {/* PUBLIC */}
      <Route
        path="/login"
        element={!user ? <Login /> : <Navigate to={getHomeRoute()} replace />}
      />
      <Route
        path="/forgot-password"
        element={!user ? <ForgotPassword /> : <Navigate to={getHomeRoute()} replace />}
      />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* 🔐 Explicit route for the lock screen (optional but handy) */}
      <Route
        path="/locked"
        element={
          user ? (
            <LockScreen onContinue={() => setShowLock(false)} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route path="/" element={<Navigate to={getHomeRoute()} replace />} />

      {/* PROTECTED: Agent area */}
      <Route
        element={
          <ProtectedRoute
            allowedRoles={[
              'agent',
              'regional',
              'territory_manager',
              'supervisor',
              'admin',
            ]}
          />
        }
      >
        <Route element={<AuthLayout />}>
          <Route path="/dashboard" element={profile?.role === 'territory_manager' ? <Navigate to="/territory/dashboard" replace /> : <Dashboard />} />
          <Route path="/sv-ar" element={<SVARPage />} />
          <Route path="/disqualified-policies" element={<DisqualifiedPolicies />} />
          <Route path="/ticketing-system" element={<TicketingSystem />} />
          <Route path="/eod-report" element={<EODReport />} />
          <Route path="/office-eods" element={<EODHistory />} />
          <Route path="/agent/violations" element={<AgentViolations />} />

          {/* ✅ ADDED: Agent tax commission log route */}
          <Route path="/agent/tax-commission" element={<AgentCommissionLog />} />

          {/* ✅ ADDED: Agent insurance commission route */}
          <Route path="/agent/commission" element={<AgentCommission />} />

          <Route path="/agent/quotes" element={<AgentQuoteLog />} />

          <Route path="/uw/submit" element={<UnderwritingSubmit />} />
        </Route>
      </Route>

      {/* PROTECTED: Underwriter area */}
      <Route
        element={
          <ProtectedRoute
            allowedRoles={['underwriter', 'uw_manager', 'supervisor', 'admin']}
          />
        }
      >
        <Route path="/uw" element={<UnderwriterLayout />}>
          <Route index element={<UnderwritingDashboard />} />
          <Route path="dashboard" element={<UnderwritingDashboard />} />
          <Route path="pending" element={<PendingUnderwriting />} />
          <Route path="log" element={<UnderwritingLog />} />
          <Route path="scoreboard" element={<UnderwritingScoreboard />} />
          <Route path="office-pacing" element={<UnderwritingOfficePacing />} />
          <Route path="retention" element={<RetentionQueue />} />

          {/* ✅ ADDED: relative child route under /uw */}
          <Route path="tax-reconciliation" element={<TaxReconciliation />} />
        </Route>
      </Route>

      {/* PROTECTED: Regional area */}
      <Route
        element={
          <ProtectedRoute
            allowedRoles={['regional', 'territory_manager', 'admin']}
          />
        }
      >
        <Route path="/regional" element={<AuthLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<RegionalDashboard />} />
          <Route path="quote-operations" element={<RegionalQuoteOperations />} />
          <Route path="office-numbers" element={<OfficeNumbers />} />
          <Route path="tickets" element={<RegionalTickets />} />
        </Route>
      </Route>

      {/* Territory managers share Regional features with explicit multi-region scope. */}
      <Route element={<ProtectedRoute allowedRoles={['territory_manager']} />}>
        <Route path="/territory" element={<AuthLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="office-numbers" element={<OfficeNumbers />} />
          <Route path="dashboard" element={<RegionalDashboard />} />
          <Route path="quote-operations" element={<RegionalQuoteOperations />} />
          <Route path="tickets" element={<RegionalTickets />} />
        </Route>
      </Route>

      {/* PROTECTED: Supervisor area */}
      <Route element={<ProtectedRoute allowedRoles={['supervisor', 'admin']} />}>
        <Route path="/supervisor" element={<AuthLayout />}>
          <Route index element={<SupervisorDashboard />} />
          <Route path="office-numbers" element={<OfficeNumbers />} />
          <Route path="tickets" element={<SupervisorTickets />} />
          <Route path="tax-wip" element={<SupervisorTaxWip />} />
          <Route path="quote-operations" element={<SupervisorQuoteOperations />} />
        </Route>
      </Route>

      {/* PROTECTED: Admin area */}
      <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="tickets" element={<AdminTickets />} />
          <Route path="office-numbers" element={<OfficeNumbers />} />
          <Route path="manage-users" element={<AdminManageUsers />} />
          <Route path="violations" element={<ManageViolations />} />
          <Route path="enter-violation" element={<EnterViolation />} />
          <Route path="office-eods" element={<OfficeEODs />} />
          <Route path="office-eods/:reportId" element={<OfficeEODs />} />

          {/* ✅ ADDED: Admin Commission Log Route */}
          <Route path="commission" element={<AdminCommissionLog />} />

          {/* ✅ ADDED: Admin Commission Upload Route */}
          <Route path="commission-upload" element={<CommissionUploader />} />

          {/* ✅ ADDED: Marketing Operations Center Route */}
          <Route path="marketing" element={<MarketingOps />} />

          {/* ✅ ADDED: Admin Tax WIP Route */}
          <Route path="tax-wip" element={<AdminTaxWip />} />

          {/* ✅ ADDED: Admin Quote Log Route */}
          <Route path="quote-log" element={<AdminQuoteLog />} />

          {/* Office Management */}
          <Route path="offices" element={<OfficeManagement />} />
          <Route path="offices/contacts" element={<CompanyContacts />} />
          <Route path="offices/company-services" element={<CompanyServices />} />
          <Route path="offices/service-providers" element={<ServiceProviders />} />
          <Route path="offices/landlords" element={<LandlordDirectory />} />
          <Route path="offices/renewals" element={<CompanyServices renewalsOnly />} />
          <Route path="offices/:officeId" element={<OfficeProfile />} />
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<div>404 - Page Not Found</div>} />
    </Routes>
  );
}

// The main App component now correctly wraps everything in the AuthProvider.
function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
